import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { fromFrame, PLUGIN_ORIGIN, pluginText, pluginUrl, readerData } from './pluginFrame';
import { pluginEnabledIn } from './usePlugins';

/** How long a plugin page may take to say it is ready. */
const READY_TIMEOUT_MS = 10000;
/** Space kept free below the frame. */
const BOTTOM_GAP_PX = 16;
/** How often the space is measured again: the page above the frame may still change. */
const SPACE_CHECK_MS = 500;

/**
 * The height the page may fill: from the frame's top (with the article
 * scrolled to the start) down to the bottom of the window, but at least half
 * the window.
 */
function frameSpace(frame) {
  const top = frame ? frame.getBoundingClientRect().top + window.scrollY : 0;
  return Math.round(Math.max(window.innerHeight * 0.5, window.innerHeight - top - BOTTOM_GAP_PX));
}

/**
 * A plugin's block in an article, drawn by the plugin's viewer page in a
 * frame that grows with it. The page gets the block's data - without the
 * fields only the author needs - and the space it may take; it reports its
 * height. A plugin that is not switched on in the article's department shows a
 * note instead.
 */
function PluginBlockView({ plugin, data, departmentId = null }) {
  const { t, i18n } = useTranslation();
  const frame = useRef(null);
  const [height, setHeight] = useState(240);
  const [ready, setReady] = useState(false);
  const [missing, setMissing] = useState(false);
  const enabled = pluginEnabledIn(plugin, departmentId);
  const name = pluginText(plugin.name, i18n.language);

  useEffect(() => {
    if (!enabled) return undefined;

    let sentSpace = null;
    function send() {
      sentSpace = frameSpace(frame.current);
      frame.current?.contentWindow?.postMessage(
        {
          type: 'disi:show',
          data: readerData(data, plugin.block),
          space: sentSpace,
          lang: i18n.language,
        },
        PLUGIN_ORIGIN,
      );
    }

    function onMessage(event) {
      if (!fromFrame(event, frame.current)) return;
      if (event.data.type === 'disi:ready') {
        setReady(true);
        send();
      }
      if (event.data.type === 'disi:height' && Number.isFinite(event.data.height)) {
        setHeight(Math.max(80, Math.min(event.data.height, 20000)));
      }
    }

    // The window's size and the content above the frame (title, tags, a late image) set the space
    const check = setInterval(() => {
      if (ready && frameSpace(frame.current) !== sentSpace) send();
    }, SPACE_CHECK_MS);

    window.addEventListener('message', onMessage);
    if (ready) send();
    return () => {
      clearInterval(check);
      window.removeEventListener('message', onMessage);
    };
  }, [data, enabled, i18n.language, plugin, ready]);

  useEffect(() => {
    if (!enabled || ready) return undefined;
    const timer = setTimeout(() => setMissing(true), READY_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [enabled, ready]);

  if (!enabled) {
    return (
      <p className="plugin-block plugin-block--off" role="note">
        {t('plugins.blockOff', { name })}
      </p>
    );
  }

  return (
    <section className="plugin-block" aria-label={name}>
      <iframe
        ref={frame}
        src={pluginUrl(plugin.id, departmentId, plugin.block.viewer)}
        title={name}
        style={{ height }}
        className="plugin-block__frame"
      />
      {missing && !ready && (
        <p className="plugin-block__missing" role="status">{t('plugins.unavailable', { name })}</p>
      )}
    </section>
  );
}

export default PluginBlockView;
